"use client";

import { Pagination as PaginationType } from "@nimble/candidate-api/types";
import { usePathname, useSearchParams } from "next/navigation";
import { useCallback } from "react";

import Pagination from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/pagination";
import ResultsPreviewSignUpCard from "@/components/shared/auth-paywall/results-preview-signup-card";
import { withAuthGate } from "@/components/shared/auth-paywall/with-auth-gate";
import { User } from "@/lib/types/auth";

interface PaginationClientProps {
  currentPage: number;
  totalPages: number;
  prevButtonDataTestId: string;
  nextButtonDataTestId: string;
}

export function PaginationClient({
  currentPage,
  totalPages,
  prevButtonDataTestId,
  nextButtonDataTestId,
}: PaginationClientProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const getPageUrl = useCallback(
    (page: number) => {
      const newParams = new URLSearchParams(searchParams);

      newParams.set("page", page.toString());

      return `${pathname}?${decodeURIComponent(newParams.toString())}`;
    },
    [searchParams, pathname],
  );

  return (
    <Pagination
      currentPage={currentPage}
      totalPages={totalPages}
      getPageUrl={getPageUrl}
      prevButtonDataTestId={prevButtonDataTestId}
      nextButtonDataTestId={nextButtonDataTestId}
    />
  );
}

export const PaginationClientWithAuthGate = withAuthGate(
  ({
    pagination,
    prevButtonDataTestId,
    nextButtonDataTestId,
  }: {
    pagination: PaginationType;
    user: User;
    prevButtonDataTestId: string;
    nextButtonDataTestId: string;
  }) => (
    <PaginationClient
      currentPage={pagination.page}
      totalPages={pagination.total_pages}
      prevButtonDataTestId={prevButtonDataTestId}
      nextButtonDataTestId={nextButtonDataTestId}
    />
  ),
  {
    customPaywall: ({ pagination }) => (
      <ResultsPreviewSignUpCard
        remainingCount={Math.max(pagination.total - pagination.per_page, 0)}
      />
    ),
  },
);
